import * as React from 'react';
import MediaQuery from 'react-responsive';
import {classNames} from '@shopify/react-utilities/styles';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {BuyerPublic} from 'interfaces/User';
import {SocialLink} from 'components/UIKit/Types';
import {Avatar, Text} from 'components/UIKit';
import {BREAKPOINTS, initials} from 'utilities';
import {Login, LoginMethod} from 'components/Login';
import {Logo, NavSearch, NavDrawer, NavItem, MegaNavPopover, UserLinks} from './components';
import {NavItemData} from './types';
import {getNavData} from './navApi';
import * as styles from './Nav.scss';
import * as locales from './locales';

interface NavData {
  loginMethods: LoginMethod[];
  searchAction: string;
  infoNavItems: NavItemData[];
  callsToAction: NavItemData[];
  socialLinks: SocialLink[];
  userLinks: NavItemData[];
}

interface NavProps {
  ariaLabel?: string;
  itemScope?: boolean;
  itemType?: string;
  role?: string;
  navData: NavData;
  currentUser: BuyerPublic | null;
}

interface NavState {
  drawerOpen: boolean;
  megaNavOpen: boolean;
  userLinksActive: boolean;
  shopNavItems: NavItemData[];
  openDesktopNavs: string[];
  openMobileNavs: string[];
  loginOpen: boolean;
}

function setBodyOverflow(visible: boolean) {
  document.body.style.overflow = visible ? 'visible' : 'hidden';
  document.body.style.position = visible ? 'static' : 'relative';
}

const DESKTOP_MIN_WIDTH = BREAKPOINTS.desktop.min + 1;
const TABLET_MAX_WIDTH = BREAKPOINTS.tablet.max;

const disableOverflow = setBodyOverflow.bind(null, false);
const enableOverflow = setBodyOverflow.bind(null, true);

const LOGIN_HASH = '#login';
const SIGNUP_HASH = '#signup';

export class NavComponent extends React.Component<NavProps & WithI18nProps, NavState> {
  static defaultProps = {
    itemScope: true,
    itemType: 'https://schema.org/SiteNavigationElement',
    role: 'navigation',
  };

  state: NavState = {
    drawerOpen: false,
    megaNavOpen: false,
    userLinksActive: false,
    shopNavItems: [],
    openDesktopNavs: [],
    openMobileNavs: [],
    loginOpen: false,
  };

  componentDidMount() {
    window.addEventListener('hashchange', this.handleHashChange);
    this.handleHashChange();
    this.fetchMegaData();
  }

  componentWillUnmount() {
    window.removeEventListener('hashchange', this.handleHashChange);
  }

  fetchMegaData = async () => {
    const shopNavItems = await getNavData();
    this.setState({shopNavItems});
  };

  handleHashChange = () => {
    const {hash} = window.location;

    if (hash === LOGIN_HASH || hash === SIGNUP_HASH) {
      this.setState(
        {
          loginOpen: true,
          drawerOpen: false,
        },
        disableOverflow,
      );
    } else {
      this.setState({loginOpen: false}, enableOverflow);
    }
  };

  handleCloseModal = () => {
    window.location.hash = '';
    this.setState({loginOpen: false});
  };

  handleDrawerOpen = () => this.setState({drawerOpen: true}, disableOverflow);
  handleDrawerClose = () => this.setState({drawerOpen: false, openMobileNavs: []}, enableOverflow);

  handleMegaNavOpen = () => this.setState({megaNavOpen: true});
  handleMegaNavClose = () => this.setState({megaNavOpen: false, openDesktopNavs: []});

  handleActiveDesktopNavs = (itemPath) => this.setState({openDesktopNavs: itemPath});
  handleActiveMobileNavs = (itemPath) => this.setState({openMobileNavs: itemPath});

  handleUserLinksOpen = () => this.setState({userLinksActive: true});
  handleUserLinksClose = () => this.setState({userLinksActive: false});

  handleBackButton = () =>
    this.setState(({openMobileNavs: navs}) => ({
      openMobileNavs: navs.slice(0, navs.length - 1),
    }));

  render() {
    const {
      megaNavOpen,
      userLinksActive,
      openDesktopNavs,
      openMobileNavs,
      drawerOpen,
      loginOpen,
      shopNavItems,
    } = this.state;

    const {i18n, role, itemType, itemScope, ariaLabel, currentUser} = this.props;

    const {
      socialLinks,
      loginMethods,
      searchAction,
      infoNavItems,
      callsToAction,
      userLinks,
    } = this.props.navData;

    return (
      <nav
        id="ExchangeMainNav"
        aria-label={ariaLabel || i18n.translate('fallbackAria')}
        className={styles.nav}
        itemScope={itemScope}
        itemType={itemType}
        role={role}
      >
        <Text text={i18n.translate('heading')} component="h2" hidden />
        <a
          data-ga-event="Main Nav"
          data-ga-action="Skip to content"
          href="#Main"
          className={styles.skip}
        >
          {i18n.translate('skip')}
        </a>

        <MediaQuery maxWidth={TABLET_MAX_WIDTH}>
          <NavDrawer
            open={drawerOpen}
            openNavs={openMobileNavs}
            onOpen={this.handleDrawerOpen}
            onBack={this.handleBackButton}
            onCancel={this.handleDrawerClose}
            onActiveNavs={this.handleActiveMobileNavs}
            shopNavItems={shopNavItems}
            infoNavItems={infoNavItems}
            callsToAction={callsToAction}
            socialLinks={socialLinks}
            userLinks={userLinks}
            user={Boolean(currentUser)}
          />
        </MediaQuery>

        <Logo />

        <div className={classNames(styles.navGroup, styles.navGroupLeft)}>
          <MediaQuery minDeviceWidth={DESKTOP_MIN_WIDTH} values={{deviceWidth: DESKTOP_MIN_WIDTH}}>
            <ul className={classNames(styles.navItems, styles.desktopNav)}>
              <MegaNavPopover
                label={i18n.translate('browse')}
                open={megaNavOpen}
                openNavs={openDesktopNavs}
                onOpen={this.handleMegaNavOpen}
                onClose={this.handleMegaNavClose}
                onActiveNavs={this.handleActiveDesktopNavs}
                shopNavItems={shopNavItems}
                infoNavItems={infoNavItems}
              />
            </ul>
          </MediaQuery>

          <NavSearch action={searchAction} className={styles.hasVerticalSpacer} />
        </div>

        <div className={classNames(styles.navGroup, styles.navGroupRight)}>
          <ul className={classNames(styles.navItems, styles.desktopNav)}>
            {callsToAction.map((cta) => (
              <NavItem
                key={cta.internalName}
                url={cta.url}
                label={cta.title}
                display={cta.button ? 'button' : undefined}
                tracking={cta.tracking}
              />
            ))}
            {currentUser && (
              <UserLinks
                user={currentUser}
                links={userLinks}
                ariaLabel={i18n.translate('userMenu')}
                dropdownActive={userLinksActive}
                wrapperClassName={styles.hasVerticalSpacer}
                onOpen={this.handleUserLinksOpen}
                onClose={this.handleUserLinksClose}
              />
            )}
          </ul>

          {currentUser && (
            <Avatar
              className={classNames(styles.deviceNav, styles.deviceAvatar)}
              imageSrc={currentUser.avatar}
              fallback={initials(currentUser.name)}
              size="small"
            />
          )}
        </div>
        <Login methods={loginMethods} show={loginOpen} onClose={this.handleCloseModal} />
      </nav>
    );
  }
}

export const Nav = withI18n({
  id: 'Nav',
  fallback: locales.en,
})(NavComponent);
